module Meevo.Components.Controls {

    import FileTypes = Meevo.Model.FileTypes;

    class MultipleFileUploadComponent implements angular.IComponentController {
        fileType: FileTypes;
        model: Meevo.Model.ICustomDataResource[];
        disabled: boolean;

        // Scope needed to fetch a document through the API. The endpoint requires all
        // four: entityId, appAreaEnum, tab_TId and tabTypeEnum. A null tab id or tab
        // type returns no controls, so the authorization set comes back empty and the
        // request 404s -- see Docs-Working/SecureBucket/plan-phase-b2.md.
        control: Meevo.Model.IUserDefinedControl;
        entityId: Guid | string;
        appAreaEnum: Meevo.Model.AppAreaEnum;

        private objectUrls: string[] = [];

        static $inject = [
            '$scope',
            '$mdDialog',
            'meevo.components.modal.ModalService',
            '$http',
            '$window',
        ];

        constructor(
            private $scope: ng.IScope,
            private $mdDialog: ng.material.IDialogService,
            private modalService: Meevo.Components.Modal.IModalService,
            private $http: ng.IHttpService,
            private $window: ng.IWindowService
        ) {
        }

        openUploadDialog() {
            this.$scope.fileType = this.fileType;
            this.$scope.updateGuid = true;
            this.$scope.meevoFile = null;

            let options = {
                scope: this.$scope,
                templateUrl: 'app/components/fileUpload/fileUploadModalTemplate.html',
                controller: 'meevo.components.fileupload.FileUploadModalController',
                controllerAs: 'vm',
                preserveScope: true,
                skipHide: true,
                multiple: true,
                additionalClassName: 'file-upload-modal',
            };
            this.$mdDialog.show(options)
                .then((uploadData) => {
                    this.model.push({
                        resourceType: this.fileType,
                        meevoFile: uploadData.meevoFile,
                        fileGuid: uploadData.fileGuid,
                        contentType: uploadData.mimeType,
                        isStorageUpdated: uploadData.isStorageUpdated,
                    })
                });
        }

        /**
         * Opens an uploaded document.
         *
         * Fetched as a blob through the API rather than linked directly: once
         * CustomDataFile moves to the secure bucket there is no publicly fetchable URL,
         * and auth is a Bearer header that a browser cannot attach to an <a href>. The
         * JwtInterceptor registered in app.config adds it to this $http call.
         */
        openFile(data: Meevo.Model.ICustomDataResource) {
            if (!data || !data.fileGuid) {
                return;
            }

            this.$http.post('/api/CustomData/Answer/FileContent', {
                EntityId: this.entityId,
                AppAreaEnum: this.appAreaEnum,
                TabId: this.control ? this.control.tab_TId : null,
                TabTypeEnum: this.control ? this.control.tabTypeEnum : null,
                FileGuid: data.fileGuid
            }, { responseType: 'blob' })
                .then((response: any) => {
                    const objectUrl = this.$window.URL.createObjectURL(response.data);
                    this.objectUrls.push(objectUrl);

                    // A blob url carries no file name, so window.open would save it as the
                    // blob's own uuid with no extension. The name has to come from the
                    // Content-Disposition the API sends -- the extension is discovered
                    // server-side from storage and is not held anywhere on the answer, so
                    // the client cannot construct it. The display name is the fallback.
                    const fileName = this.parseFileName(response.headers('content-disposition'))
                                     || this.getFileName(data);

                    const link = this.$window.document.createElement('a');
                    link.href = objectUrl;
                    link.download = fileName;
                    this.$window.document.body.appendChild(link);
                    link.click();
                    this.$window.document.body.removeChild(link);
                })
                .catch(() => this.modalService.warning('MESSAGE_FILE_UNAVAILABLE', 0, true, {}));
        }

        /**
         * Pulls the file name out of a Content-Disposition value. Prefers the RFC 6266
         * `filename*` form, which carries the real UTF-8 name, over the sanitised ASCII
         * `filename`. Returns null rather than a partial guess when neither parses.
         */
        private parseFileName(contentDisposition: string): string {
            if (!contentDisposition) {
                return null;
            }

            const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
            if (encoded && encoded[1]) {
                try {
                    return decodeURIComponent(encoded[1].trim());
                } catch (e) {
                    // Fall through to the plain form rather than failing the download.
                }
            }

            const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);

            return plain && plain[1] ? plain[1].trim() : null;
        }

        $onDestroy() {
            this.objectUrls.forEach((url) => this.$window.URL.revokeObjectURL(url));
            this.objectUrls = [];
        }

        getFileName(data: Meevo.Model.ICustomDataResource) {
            return Meevo.Model.StorageFileHelper.fileName(data);
        }

        remove(id: string) {
            if (this.disabled) {
                return;
            }

            const modalOptions = {
                showCancelButton: true,
                cancelButtonContentKey: 'LABEL_NO',
                cancelButtonHotKey: 'MODAL_NO_BUTTON',
                okButtonHotKey: 'MODAL_YES_BUTTON',
                okButtonContentKey: 'LABEL_YES'
            };
            this.modalService
                .warning('MESSAGE_DELETE_CONFIRMATION', 0, true, modalOptions)
                .then((confirmResult: boolean) => {
                    if (!confirmResult) {
                        return;
                    }

                    this.model = this.model.filter((item) => item.fileGuid !== id);
                });
        }
    }


    angular.module('meevo.components.controls')
        .component('meevoMultipleFileUploadComponent', <angular.IComponentOptions>{
            templateUrl: 'app/components/controls/multiple-file-upload/multiple-file-upload.component.html',
            controller: MultipleFileUploadComponent,
            controllerAs: '$ctrl',
            bindings: {
                model: '=ngModel',
                fileType: '=',
                disabled: '=?ngDisabled',
                control: '<?',
                entityId: '<?',
                appAreaEnum: '<?'
            }
        });
}
